import { useState, type FormEvent } from 'react';
import { ContactRequest } from '@im-marina/shared';
import styles from './ContactForm.module.css';

type Status =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent' }
  | { state: 'error'; message: string };

type FieldErrors = Partial<Record<'name' | 'email' | 'subject' | 'message', string>>;

const MESSAGE_MAX = 3500;

export function ContactForm() {
  // Rendered-at timestamp lets the API reject submissions made implausibly fast (bots).
  const [renderedAt] = useState(() => Date.now());
  const [status, setStatus] = useState<Status>({ state: 'idle' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [messageLength, setMessageLength] = useState(0);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;

    const parsed = ContactRequest.safeParse({
      name: data.name,
      email: data.email,
      subject: data.subject || undefined,
      message: data.message,
      website: data.website,
      renderedAt,
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof FieldErrors;
        next[field] ??= messageFor(field);
      }
      setErrors(next);
      form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }

    setErrors({});
    setStatus({ state: 'sending' });
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) throw new Error(res.status === 429 ? 'rate' : 'http');
      setStatus({ state: 'sent' });
      form.reset();
      setMessageLength(0);
    } catch (err) {
      setStatus({
        state: 'error',
        message:
          err instanceof Error && err.message === 'rate'
            ? 'Too many messages in a short time. Please try again later.'
            : 'Your message could not be sent. Please try again, or email directly.',
      });
    }
  }

  if (status.state === 'sent') {
    return (
      <div className={styles.sent} role="status">
        <p className={styles.sentTitle}>Thank you</p>
        <p>Your message has been sent. I will reply as soon as I can.</p>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => setStatus({ state: 'idle' })}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <Field label="Name" name="name" error={errors.name} autoComplete="name" required />
      <Field
        label="Email"
        name="email"
        type="email"
        error={errors.email}
        autoComplete="email"
        required
      />
      <Field label="Subject" name="subject" error={errors.subject} />
      <div className={styles.field}>
        <label htmlFor="message" className={styles.label}>
          Message
        </label>
        <textarea
          id="message"
          name="message"
          rows={7}
          maxLength={MESSAGE_MAX}
          required
          aria-invalid={errors.message ? true : undefined}
          aria-describedby="message-help"
          className={styles.input}
          onChange={(e) => setMessageLength(e.target.value.length)}
        />
        <span id="message-help" className={errors.message ? styles.error : styles.help}>
          {errors.message ?? `${messageLength} / ${MESSAGE_MAX}`}
        </span>
      </div>

      {/* Honeypot: hidden from people and assistive tech, but bots tend to fill it in. */}
      <div className={styles.trap} aria-hidden>
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      <div className={styles.actions}>
        <button type="submit" className={styles.submit} disabled={status.state === 'sending'}>
          {status.state === 'sending' ? 'Sending…' : 'Send message'}
        </button>
        {status.state === 'error' && (
          <p className={styles.error} role="alert">
            {status.message}
          </p>
        )}
      </div>
    </form>
  );
}

function messageFor(field: keyof FieldErrors): string {
  switch (field) {
    case 'name':
      return 'Please enter your name.';
    case 'email':
      return 'Please enter a valid email address.';
    case 'subject':
      return 'Please keep the subject under 200 characters.';
    case 'message':
      return 'Please enter a message.';
    default:
      return 'Please check this field.';
  }
}

type FieldProps = {
  label: string;
  name: string;
  type?: string;
  error?: string | undefined;
  autoComplete?: string;
  required?: boolean;
};

function Field({ label, name, type = 'text', error, autoComplete, required }: FieldProps) {
  const errorId = `${name}-error`;
  return (
    <div className={styles.field}>
      <label htmlFor={name} className={styles.label}>
        {label}
        {!required && <span className={styles.optional}> (optional)</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={styles.input}
      />
      {error && (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      )}
    </div>
  );
}
